<template>
  <div
    role="tablist"
    class="relative isolate flex max-w-min flex-nowrap gap-2 overflow-x-auto overflow-y-hidden font-semibold after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-obsidian-300"
  >
    <a
      v-for="(tab, i) in tabs"
      :key="i"
      :class="{
        'z-10 text-obsidian-900 after:bg-obsidian-900': activeTab === tab.id,
        'px-1': !!props.slim,
        'px-3': !props.slim,
      }"
      class="relative -mb-[1px] flex cursor-pointer items-center gap-2 whitespace-nowrap py-3 text-hazel-600 after:absolute after:inset-x-0 after:bottom-0 after:h-1 after:transition-all hover:after:bg-hazel-300"
      @click="activeTab = tab.id"
    >
      <span>{{ tab.title }}</span>
      <span
        v-if="badgeText(tab)"
        class="rounded-full px-2 py-0.5 text-xs"
        :class="
          badgeText(tab) === '0'
            ? 'bg-hazel-200 text-obsidian-800'
            : 'bg-bittersweet-100 text-bittersweet-800'
        "
        >{{ badgeText(tab) }}</span
      >
    </a>
  </div>
</template>

<script lang="ts" setup>
import { useRouteQueryStore } from '@/stores/route-query.store';
import type { MaybeRefOrGetter } from '@vueuse/core';
import { ref, toRef, toValue, watch, watchEffect } from 'vue';
import { useRoute } from 'vue-router';

export interface TabItem {
  title: MaybeRefOrGetter<string>;
  id: string;
  badge?: MaybeRefOrGetter<string | undefined>;
}

const route = useRoute();
const routeQueryStore = useRouteQueryStore();

const props = withDefaults(
  defineProps<{
    tabs: TabItem[];
    modelValue: string;
    queryParam?: string;
    /** When false, tab changes do not update the router query (for nested UI such as audit meta). */
    syncRoute?: boolean;
    slim?: boolean;
  }>(),
  { syncRoute: true },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
}>();

const isValidTabQueryParam =
  props.syncRoute &&
  props.tabs.some((tab) => tab.id === (route.query[props.queryParam ?? 'tab'] as string));
const presetTab = isValidTabQueryParam
  ? (route.query[props.queryParam ?? 'tab'] as string)
  : props.modelValue;
const activeTab = ref<string>(presetTab);

watch(toRef(props, 'modelValue'), (newModelValue) => {
  activeTab.value = newModelValue;
});

watchEffect(async () => {
  emit('update:modelValue', activeTab.value);
  if (props.syncRoute) {
    await routeQueryStore.patch({ [props.queryParam ?? 'tab']: activeTab.value });
  }
});

const badgeText = (tab: TabItem) => toValue(tab.badge);
</script>
